import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@london-life/ui/components/dialog';
import { TIE_LABELS, formatPence, type PersonView, type SeatOffer } from '@london-life/contracts';
import { api } from '../lib/api';
import { PersonPicture } from '../people/person-card';

const nameOf = (person: Pick<PersonView, 'username' | 'firstName'>) =>
  person.username ? `@${person.username}` : person.firstName;
// who a seat can go to: friends and closer, not blocked
const CLOSE = new Set<PersonView['tie']>(['friend', 'close_friend', 'partner', 'family']);
const TIE_ICONS: Partial<Record<PersonView['tie'], string>> = {
  partner: '💞',
  family: '🏡',
  close_friend: '💛',
  friend: '🙂',
};

// "Bring someone along" (as Lagos Life's): pick up to a few friends, partners or family to offer seats on this
// flight. They buy their own ticket; the jet waits at the gate for them.
export function BringAlongSheet({
  open,
  onClose,
  destination,
  seats,
  holdSeconds,
  sending,
  onOffer,
}: {
  open: boolean;
  onClose: () => void;
  destination: string;
  seats: number;
  holdSeconds: number;
  sending: boolean;
  onOffer: (lifeIds: string[]) => void;
}) {
  const [people, setPeople] = useState<PersonView[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  useEffect(() => {
    if (!open) return;
    let live = true;
    setChosen([]);
    setFailed(false);
    api.people
      .get()
      .then((reply) => live && setPeople(reply.people.filter((person) => CLOSE.has(person.tie) && !person.blocked)))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [open]);
  const toggle = (lifeId: string) =>
    setChosen((list) =>
      list.includes(lifeId) ? list.filter((id) => id !== lifeId) : list.length >= seats ? list : [...list, lifeId],
    );
  const minutes = Math.round(holdSeconds / 60);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="city-card city-phone flights-sheet">
        <DialogTitle>👥 Bring someone along</DialogTitle>
        <DialogDescription>
          Your flight to {destination} · up to {seats} · they buy their own ticket
        </DialogDescription>
        {failed ? (
          <p className="neighbours-note">Couldn&apos;t load your people right now. Try again in a moment.</p>
        ) : !people ? (
          <p className="neighbours-note">Loading your people…</p>
        ) : !people.length ? (
          <p className="neighbours-note">
            Seats go to friends, partners and family. Spend time with people around London to get close.
          </p>
        ) : (
          <div className="bring-along-list" role="group" aria-label="Who to bring">
            {people.map((person) => {
              const on = chosen.includes(person.lifeId);
              return (
                <button
                  type="button"
                  key={person.lifeId}
                  aria-pressed={on}
                  className={on ? 'active' : ''}
                  onClick={() => toggle(person.lifeId)}
                >
                  <PersonPicture person={person} />
                  <span className="flights-to-name">
                    <strong>{nameOf(person)}</strong>
                    <small>
                      {TIE_ICONS[person.tie]} {TIE_LABELS[person.tie]}
                    </small>
                  </span>
                  <em aria-hidden="true">{on ? '✓' : ''}</em>
                </button>
              );
            })}
          </div>
        )}
        <p className="flights-perk">
          The jet waits at the gate up to {minutes} minute{minutes === 1 ? '' : 's'} for them to hop in.
        </p>
        <button
          type="button"
          className="city-card-go"
          disabled={!chosen.length || sending}
          onClick={() => onOffer(chosen)}
        >
          {sending
            ? 'Offering…'
            : chosen.length
              ? `Offer ${chosen.length} seat${chosen.length === 1 ? '' : 's'}`
              : 'Pick who to bring'}
        </button>
      </DialogContent>
    </Dialog>
  );
}

// a seat a friend saved for me (over whatever screen is open, like a knock at the door): hop in (my own
// ticket on their flight) or not this time, before the jet leaves
export function SeatOfferAlert({
  offer,
  walletPence,
  busy,
  onAnswer,
}: {
  offer: SeatOffer;
  walletPence: number | null;
  busy: boolean;
  onAnswer: (take: boolean) => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    navigator.vibrate?.([60, 80, 60]);
  }, [offer.id]);
  const left = Math.max(0, Math.ceil((Date.parse(offer.expiresAt) - now) / 1000));
  if (!left) return null;
  const short = walletPence !== null && walletPence < offer.farePence;
  return (
    <div className="knock-alert seat-offer" role="alertdialog" aria-label={`${offer.host} saved you a seat`}>
      <span className="seat-offer-icon" aria-hidden="true">
        ✈️
      </span>
      <div className="knock-alert-text">
        <strong>{offer.host} saved you a seat</strong>
        <small>
          {offer.number} · {offer.from.code} → {offer.to.code} · {formatPence(offer.farePence)} ·{' '}
          {left < 60 ? `${left}s` : `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`}
        </small>
      </div>
      <div className="knock-alert-actions">
        <button type="button" className="knock-no" disabled={busy} onClick={() => onAnswer(false)}>
          Not now
        </button>
        <button type="button" className="knock-yes" disabled={busy || short} onClick={() => onAnswer(true)}>
          {short ? 'Short' : 'Hop in'}
        </button>
      </div>
    </div>
  );
}
